// Выдать человеку вход: одна процедура на карточку и на раздачу пачкой.
//
// ## Почему процедура, а не две формы
//
// Заказчик 14.09.2026: «концепция замудрённая, я там теряюсь». Теряться было в чём: чтобы
// человек вошёл, требовалось три захода в разные места — завести учётную запись в настройках,
// вернуться и назначить роль, вернуться ещё раз и нажать «Дать код». Причём в таком порядке:
// сначала запись, роль потом. Код, выданный до роли, открывает человеку вход в систему, где
// ему не видно ничего, — и выглядит это как поломка программы, а не как недоделанная
// настройка.
//
// Здесь порядок обратный и единственный: РОЛЬ, потом ссылка. Всё, что между ними, —
// заведение записи, подбор свободного имени входа, выпуск кода — делается за один раз и
// одинаково в обоих местах, откуда доступ выдают.
//
// ## Своих дверей на сервере не заводили
//
// Процедура складывается из трёх уже существующих: завести пользователя, заменить роли,
// выпустить код. Четвёртая дверь «сделай всё сразу» означала бы второй набор правил про
// занятое имя входа, про отключённую запись и про роль старше своей — а они уже есть и
// проверены тестами прав.

import {
  createUser,
  issueInvite,
  setUserRoles,
  type UserOut,
} from '@/shared/api/endpoints/users';
import { ApiError } from '@/shared/errors/api-error';

/** Сколько раз пробуем подобрать свободное имя входа, прежде чем сдаться и сказать словами. */
const ПОПЫТОК_ИМЕНИ = 5;

export interface КогоПриглашаем {
  employeeId: string;
  /** Как человек записан в реестре: «Багровский С. В.». Он же станет именем в системе. */
  фамилия: string;
  /** Учётная запись, если она уже есть. `null` — человека в системе ещё нет. */
  запись: UserOut | null;
}

export interface НастройкиВыдачи {
  /** Роли, которые человек получит. Пустой список законен только у уже заведённого. */
  роли: readonly string[];
  /** Пускать ли в программу на компьютере. У охранника это всегда нет. */
  вПрограмму: boolean;
  /** Есть ли право менять роли (`core.role.assign`). Нет — роли не трогаем вовсе. */
  можноРоли: boolean;
  /** Есть ли право заводить учётные записи (`core.user.manage`). */
  можноЗаводить: boolean;
  /**
   * Менять ли роль тому, у кого она УЖЕ есть.
   *
   * В карточке человека — да: там роль выбирают ему лично, видя его нынешние отметки, и смена
   * роли это осознанное действие.
   *
   * В раздаче пачкой — НЕТ, и это не мелочь. Роль там одна на всех выбранных, и старшему
   * объекта, случайно попавшему в список охранников, она молча заменила бы его собственную:
   * роли заменяются целиком, а замена вдобавок поднимает версию учётной записи и выбрасывает
   * человека из всех его входов. Приглашение — это про «дать войти», а не про «переназначить».
   * Тому, у кого ролей нет вовсе, роль ставится в обоих случаях: это не замена, а недостающее.
   */
  менятьРольСуществующему: boolean;
}

export interface ИтогВыдачи {
  employeeId: string;
  фамилия: string;
  логин: string | null;
  /** Ссылка целиком. Её собирает сервер: домен стенда и боевого разные. */
  ссылка: string | null;
  код: string | null;
  годенДо: string | null;
  /** Почему не вышло. Заполнено ровно тогда, когда ссылки нет. */
  причина: string | null;
}

/**
 * Имя входа из того, как человек записан в реестре.
 *
 * У заказчика фамилии записаны инициалами — «Багровский С. В.», — и это не мелочь: двое
 * однофамильцев с одинаковыми инициалами в базе уже есть. Поэтому вторая и следующие попытки
 * не «что-нибудь другое», а тот же человек с номером: имя остаётся читаемым, а занятое
 * разводится само.
 *
 * Латиница здесь не нужна: имя входа принимается и кириллицей, а переписанная латиницей
 * фамилия перестаёт узнаваться тем, кто её диктует.
 */
export function имяВхода(фамилия: string, попытка = 0): string {
  const основа = фамилия
    .toLowerCase()
    .replace(/[^0-9a-zа-яё]+/gi, '.')
    .replace(/^\.+|\.+$/g, '');
  // Сервер не принимает имя короче трёх знаков. Пустая или односложная подпись бывает у
  // перенесённых из книг строк, и падать на ней нельзя — человека всё равно надо пустить.
  const годная = основа.length >= 3 ? основа : `${основа}.вход`;
  return попытка === 0 ? годная : `${годная}-${попытка + 1}`;
}

/** Занято ли имя входа: сервер называет виноватое поле, а не только код ошибки. */
function имяЗанято(ошибка: unknown): boolean {
  return ApiError.is(ошибка) && ошибка.envelope.fields?.login !== undefined;
}

/** Причина словами, как её сказал сервер. Свою формулировку не выдумываем. */
function причина(ошибка: unknown): string {
  if (ApiError.is(ошибка)) {
    const конверт = ошибка.envelope;
    return конверт.hint === undefined ? конверт.message : `${конверт.message}: ${конверт.hint}`;
  }
  return 'не вышло связаться с сервером';
}

/** Одинаковы ли наборы ролей. Порядок значения не имеет: роли заменяются целиком. */
function тоЖеСамое(было: readonly string[], стало: readonly string[]): boolean {
  return (
    было.length === стало.length && [...было].sort().join() === [...стало].sort().join()
  );
}

async function завестиЗапись(
  кого: КогоПриглашаем,
  настройки: НастройкиВыдачи,
): Promise<UserOut> {
  let последняя: unknown = null;
  for (let попытка = 0; попытка < ПОПЫТОК_ИМЕНИ; попытка += 1) {
    try {
      return await createUser({
        login: имяВхода(кого.фамилия, попытка),
        display_name: кого.фамилия,
        employee_id: кого.employeeId,
        roles: [...настройки.роли],
        cabinet_access: настройки.вПрограмму,
      });
    } catch (ошибка) {
      последняя = ошибка;
      // Занятое имя разводим сами: у заказчика однофамильцы с одинаковыми инициалами, и
      // заставлять человека придумывать имя входа за каждого из двадцати — это ровно тот
      // путь, из-за которого раздача доступов и не делалась.
      if (!имяЗанято(ошибка)) {
        throw ошибка;
      }
    }
  }
  throw последняя;
}

/**
 * Обеспечить человеку вход и вернуть ссылку.
 *
 * Отказы не бросаются наружу: у раздачи пачкой один сорвавшийся человек не имеет права
 * прекратить раздачу остальным. Строка есть у каждого, и у сорвавшегося написано, почему.
 */
export async function выдатьДоступ(
  кого: КогоПриглашаем,
  настройки: НастройкиВыдачи,
): Promise<ИтогВыдачи> {
  const пусто = { employeeId: кого.employeeId, фамилия: кого.фамилия };
  try {
    let запись = кого.запись;
    if (запись === null) {
      if (!настройки.можноЗаводить) {
        return {
          ...пусто,
          логин: null,
          ссылка: null,
          код: null,
          годенДо: null,
          причина:
            'учётной записи нет, а заводит её администратор или финансовый директор — ' +
            'попросите их, вход человеку вы потом откроете сами',
        };
      }
      запись = await завестиЗапись(кого, настройки);
    } else if (
      настройки.можноРоли &&
      настройки.роли.length > 0 &&
      (настройки.менятьРольСуществующему || запись.roles.length === 0) &&
      !тоЖеСамое(запись.roles, настройки.роли)
    ) {
      // Роли ЗАМЕНЯЮТСЯ целиком — так устроена дверь, и так же устроен экран настроек.
      запись = await setUserRoles(запись.id, [...настройки.роли]);
    }

    const выпуск = await issueInvite(запись.id);
    return {
      ...пусто,
      логин: запись.login,
      ссылка: выпуск.link,
      код: выпуск.code,
      годенДо: выпуск.expires_at,
      причина: null,
    };
  } catch (ошибка) {
    return {
      ...пусто,
      логин: null,
      ссылка: null,
      код: null,
      годенДо: null,
      причина: причина(ошибка),
    };
  }
}

/**
 * Список «фамилия и ссылка» одним куском текста — тем, что уедет в буфер обмена.
 *
 * Ради этого всё и просили: заказчик разом рассылает вход своим людям, а рассылает он их
 * руками, из своей переписки. Пропущенные в текст не идут: он его перешлёт целиком, и строка
 * «Иванов — не вышло» уйдёт вместе со ссылками. Причины пропуска остаются на экране.
 */
export function списокСсылок(итоги: readonly ИтогВыдачи[]): string {
  return итоги
    .filter((итог) => итог.ссылка !== null)
    .map((итог) => `${итог.фамилия} — ${итог.ссылка ?? ''}`)
    .join('\n');
}
